import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

import { trackEvent } from "@/lib/mixpanel";

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
}

/**
 * FEAT-003 (docs/features/FEAT-003-trade-route-unmapped-market-crash.md):
 * route-level error boundary. Wraps the routed page tree (inside AppShell's
 * `<Outlet/>`, below Header/Footer) so a render throw in any page degrades
 * to a fallback panel instead of blanking the whole SPA — the previously
 * missing catch that let the /trade unmapped-market crash take down the
 * entire app. Header/Footer stay mounted and interactive because this
 * boundary only wraps the routed content, not the shell around it.
 *
 * Only `UserMenuErrorBoundary` (components/layout/Header.tsx) existed
 * before this; that one is scoped to the user menu, not the route tree.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("RouteErrorBoundary caught:", error, info);
    try {
      trackEvent("Route Error", {
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
      });
    } catch (trackingError) {
      // A blocked/failing analytics SDK (ad blockers, privacy mode) must
      // never escape componentDidCatch — that would defeat the whole point
      // of this boundary by blanking the page it exists to protect.
      console.warn("RouteErrorBoundary: trackEvent failed", trackingError);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <h1 className="text-xl font-bold text-slate-900">Something went wrong</h1>
            <p className="mt-2 text-sm text-slate-600">
              An unexpected error occurred while rendering this page. Reloading usually
              fixes it.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
